import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
// Routing
import { Router } from '@angular/router';
// Controllers
import { LoadingController } from '@ionic/angular';
// Services
import { DroneOperatorService } from 'src/app/services/drone-operator.service';
import { FilesService } from 'src/app/services/files.service';
import { HttpResponsesService } from 'src/app/services/httpResponses.service';
// Models
import { User, Pilot, Vehicle, FlightSession, DroneOperator } from '@mocca-technologies/model';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { LoadingGuardService } from 'src/app/services/loading-guard.service';


@Component({
  selector: 'app-pilot-list',
  templateUrl: './pilot-list.component.html',
  styleUrls: ['./pilot-list.component.scss'],
})
export class PilotListComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * Full pilots list for the current context (provided by parent).
   * - Main view: Fleet passes org pilots
   * - Sub-operator view: SubOperatorsListCard passes map[operatorId]
   */
  @Input() pilots: Pilot[] = [];

  /**
   * Existing inputs (used for selection / filtering / drag rules)
   */
  @Input() selectedVehicle: Vehicle;
  @Input() selectedPilot: Pilot;
  @Input() showList: boolean = false;
  @Input() operator: DroneOperator;

  /**
   * Output selection event
   */
  @Output() selectPilotGlobally = new EventEmitter<Pilot>();

  /**
   * Internal state (render)
   */
  filteredPilotList: Pilot[] = [];
  sessionsList: FlightSession[] = [];
  user: User;
  existSubOperatorAdmin: boolean = false;

  /** Sequence counter to discard stale concurrent filter responses */
  private filterSeqId = 0;

  private destroy$ = new Subject<void>();

  // -------------------- Getters --------------------

  /** @returns number of pilots currently displayed */
  getNumPilots(): number {
    return this.filteredPilotList.length;
  }

  /** @returns true if we are in sub-operator card context */
  get isSubOperatorView(): boolean {
    return !!this.operator;
  }

  constructor(
    private droneOperatorService: DroneOperatorService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private filesService: FilesService,
    private httpResponseService: HttpResponsesService,
    private translateService: TranslateService,
  ) {}

  // -------------------- Lifecycle --------------------

  ngOnInit(): void {
    this.user = localStorage.getItem('user')
      ? JSON.parse(localStorage.getItem('user')!)
      : null;

    // Render from provided input (no HTTP here)
    this.applyPilotsInput();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pilots']) {
      this.applyPilotsInput();
    }

    // If vehicle selection changes, update filter without loading.
    // Skip when pilots also changed: applyPilotsInput() already calls the filter.
    if (changes['selectedVehicle'] && this.selectedVehicle && !changes['pilots']) {
      this.filterPilotListAccordingToSessions_NoLoader();
    }

    if (changes['selectedVehicle'] && !this.selectedVehicle) {
      // If vehicle cleared, show full list again
      this.applyPilotsInput();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Prevent leaking blob avatar URL of selected pilot
    if (this.selectedPilot?.avatarUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.selectedPilot.avatarUrl);
    }
  }

  // -------------------- Data handling (NO loaders, NO fetching if parent provides data) --------------------

  /**
   * Applies pilots input to internal list + sets flags.
   * This is the key change: list is rendered from parent data, avoiding repeated loadings.
   */
  private applyPilotsInput(): void {
    const list = Array.isArray(this.pilots) ? this.pilots : [];
    this.filteredPilotList = list;
    this.existSubOperatorAdmin = list.length > 0;
    this.cdr.detectChanges();

    // If a vehicle is selected, keep the old behavior and filter list
    if (this.selectedVehicle) {
      this.filterPilotListAccordingToSessions_NoLoader();
    }
  }

  /**
   * Filters pilot list based on sessions of selected vehicle.
   * Keeps old business logic but avoids showing loaders and avoids any "loadPilots()" calls.
   */
  private filterPilotListAccordingToSessions_NoLoader(): void {
    if (!this.selectedVehicle?.uniqueId) return;

    const seqId = ++this.filterSeqId;
    this.filteredPilotList = [];
    this.sessionsList = [];

    this.droneOperatorService
      .getVehicleSessions(this.selectedVehicle.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (seqId !== this.filterSeqId) return; // discard stale concurrent response

          const sessions = (res?.body?.responseObject || []) as FlightSession[];

          if (sessions && sessions.length > 0) {
            this.sessionsList = sessions;

            for (const pilot of this.pilots || []) {
              if (!pilot?.uniqueId) continue;
              const session = this.sessionsList.find((s) => s?.userUuid === pilot.uniqueId);
              if (!session) this.filteredPilotList.push(pilot);
            }
          } else {
            this.filteredPilotList = this.pilots || [];
          }

          this.existSubOperatorAdmin = (this.filteredPilotList?.length || 0) > 0;
          this.cdr.detectChanges();
        },
        error: (error: HttpErrorResponse) => {
          if (seqId !== this.filterSeqId) return;
          this.httpResponseService.showServerError(error?.status, error?.error);
          // fallback: show all pilots if filter fails
          this.filteredPilotList = this.pilots || [];
          this.cdr.detectChanges();
        },
      });
  }

  // -------------------- UI actions --------------------

  /**
   * Selects/deselects a pilot and emits it to the parent.
   * Fetches pilot avatar on selection (feature preserved).
   */
  selectPilot(pilot: Pilot): void {
    if (!pilot) {
      this.selectedPilot = null;
      this.selectPilotGlobally.emit(null);
      this.cdr.detectChanges();
      return;
    }

    if (pilot?.uniqueId === this.selectedPilot?.uniqueId) {
      this.selectedPilot = null;
      this.selectPilotGlobally.emit(null);
      this.cdr.detectChanges();
      return;
    }

    this.filesService
      .getPilotAvatar(pilot.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (image: Blob) => {
          if (!image || image.size === 0) {
            pilot.avatarUrl = null;
          } else {
            if (pilot.avatarUrl?.startsWith('blob:')) {
              URL.revokeObjectURL(pilot.avatarUrl);
            }
            pilot.avatarUrl = URL.createObjectURL(image);
          }

          this.selectedPilot = pilot;
          this.selectPilotGlobally.emit(pilot);
          this.cdr.detectChanges();
        },
        error: async (error: any) => {
          if (error?.status !== 422 && error?.status !== 404) {
            let body = error?.error;
            if (body instanceof Blob) {
              body = await body.text();
              try { body = JSON.parse(body); } catch {}
            }
            this.httpResponseService.showServerError(error?.status, body);
          }

          pilot.avatarUrl = null;
          this.selectedPilot = pilot;
          this.selectPilotGlobally.emit(pilot);
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Handles click anywhere on a pilot row (main fleet view only).
   */
  onPilotRowClick(pilot: Pilot): void {
    if (!this.showList && !this.selectedVehicle) {
      this.selectPilot(pilot);
    }
  }

  /**
   * Navigates to pilot form (kept original route)
   */
  goToVehicleForm(): void {
    this.router.navigate(['/pilot-form'], {
      state: { operatorId: this.operator?.uniqueId, organizationType: this.user?.organizationType },
    });
  }
}
